// Owner Offboarding: an old-model (historic) car leaving the fleet.
// db/migrations/1500, docs/OWNER-OFFBOARDING.md.
//
// Pressing Offboard is the whole start: the owner is emailed the 30 day process
// in the same transaction that opens the case, so there is no provisional date
// waiting on someone to confirm it. That is what went wrong under the 0122
// design, where LK36RJGP's letter never went out. The letter is shown in full
// before the button is pressed, rendered by the same function that sends it.
//
// Who may act is cockpit_access.can_offboard_owners, decided in Postgres; every
// refusal on this page is Postgres's sentence. CLAUDE.md rule 4.
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, Check, ChevronDown, ChevronRight, LogOut, Mail, ShieldAlert } from 'lucide-react'
import {
  useAssessOwnerOffboarding, useCloseOwnerOffboarding, useGetOwnerOffboarding, useGetOwnerOffboardingLetter,
  useRecordOwnerOffboardingStep, useStartOwnerOffboarding, useStopOwnerOffboarding,
} from '../hooks/backend/owners'
import { Button } from '../lib/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../lib/shadcn/dialog'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Spinner } from '../lib/shadcn/spinner'
import { Textarea } from '../lib/shadcn/textarea'
import { Chip } from '../components/interviews/Chip'
import { OffboardingInspection } from '../components/owners/OffboardingInspection'
import { formatDay, formatWhen } from '../utils/loans'
import {
  EMAIL_STATUS_LABEL, EXIT_REASONS, todaySast,
  type LetterPreview, type OffboardCar, type OffboardCase, type OffboardPage, type OffboardStep,
} from '../utils/ownerOffboarding'

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

export default function OwnerOffboarding() {
  const pageQuery = useGetOwnerOffboarding<OffboardPage | null>()
  const [params] = useSearchParams()
  const [expanded, setExpanded] = useState<string | null>(params.get('c'))
  const [offboarding, setOffboarding] = useState<OffboardCar | null>(null)
  const [showClosed, setShowClosed] = useState(false)

  const load = pageQuery.trigger
  const refresh = useCallback(() => { void load({}, { skipCache: true }).catch(() => undefined) }, [load])
  useEffect(() => { refresh() }, [refresh])

  const page = pageQuery.data
  if (pageQuery.loading && !page && !pageQuery.error) {
    return <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading…</div>
  }
  if (pageQuery.error && !page) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md space-y-3 rounded-lg border border-border bg-card p-6 text-center text-sm">
          <p className="text-rose-700 dark:text-rose-300">{pageQuery.error}</p>
          <Button size="sm" variant="outline" onClick={refresh}>Try again</Button>
        </div>
      </div>
    )
  }
  if (!page) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <ShieldAlert className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">Owner offboarding is open to staff logins.</p>
          <p className="text-xs text-muted-foreground">Ask an admin to switch on View for you in cockpit access.</p>
        </div>
      </div>
    )
  }

  const live = page.cases.filter((c) => !c.closed_at)
  const closed = page.cases.filter((c) => c.closed_at)

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
          <LogOut className="h-5 w-5" />
        </div>
        <div className="min-w-[12rem] flex-1">
          <h1 className="text-lg font-semibold text-foreground">Owner Offboarding</h1>
          <p className="text-xs text-muted-foreground">
            An old-model car leaving the fleet over 30 days. Pressing Offboard emails the owner what happens and puts every step on the task board.
          </p>
        </div>
      </div>
      {!page.can_act && (
        <p className="border-b border-border bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
          You can read this page. Offboarding a car and recording its steps is for {page.who_can_act ?? 'the people named in cockpit access'}.
        </p>
      )}
      {pageQuery.error && <p className="border-b border-border bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{pageQuery.error}</p>}

      <div className="min-h-0 flex-1 overflow-y-auto">
        <section className="border-b border-border">
          <h2 className="px-4 pb-1 pt-3 text-sm font-semibold text-foreground">Being offboarded ({live.length})</h2>
          {live.length === 0 ? (
            <p className="px-4 pb-4 text-xs text-muted-foreground">No car is being offboarded. Choose one below to start.</p>
          ) : (
            <ul className="divide-y divide-border">
              {live.map((c) => (
                <CaseItem key={c.id} c={c} open={expanded === c.id} canAct={page.can_act}
                  onToggle={() => setExpanded((x) => (x === c.id ? null : c.id))} onChanged={refresh} />
              ))}
            </ul>
          )}
        </section>

        <section className="border-b border-border">
          <h2 className="px-4 pb-1 pt-3 text-sm font-semibold text-foreground">Old-model cars on the fleet ({page.cars.length})</h2>
          <p className="px-4 pb-2 text-xs text-muted-foreground">Only cars on the old model are listed. A Manage My Car car leaves under its own agreement.</p>
          <ul className="divide-y divide-border">
            {page.cars.map((car) => (
              <li key={car.vehicle_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2">
                <span className="w-24 shrink-0 font-mono text-sm text-foreground">{car.plate}</span>
                <span className="min-w-[10rem] flex-1">
                  <span className="block text-sm text-foreground">{car.owner_name ?? 'No owner on file'}</span>
                  <span className="block break-all text-xs text-muted-foreground">
                    {[car.vehicle_name, car.manager_name ? `managed by ${car.manager_name}` : null, car.owner_email ?? 'no email on file'].filter(Boolean).join(', ')}
                  </span>
                </span>
                <span className="flex flex-wrap items-center gap-1">
                  {car.status === 'offboarding' && (
                    <Chip tone="amber" icon={<AlertTriangle className="h-3 w-3" />}>Marked offboarding, owner not told here</Chip>
                  )}
                  {car.off_road && <Chip tone="neutral">Off the road</Chip>}
                  {page.can_act && (
                    <Button size="sm" variant="outline" onClick={() => setOffboarding(car)}>Offboard</Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>

        {closed.length > 0 && (
          <section>
            <button type="button" onClick={() => setShowClosed((v) => !v)} className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-semibold text-foreground">
              {showClosed ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              Closed in the last 60 days ({closed.length})
            </button>
            {showClosed && (
              <ul className="divide-y divide-border">
                {closed.map((c) => (
                  <CaseItem key={c.id} c={c} open={expanded === c.id} canAct={false}
                    onToggle={() => setExpanded((x) => (x === c.id ? null : c.id))} onChanged={refresh} />
                ))}
              </ul>
            )}
          </section>
        )}
      </div>

      <StartDialog car={offboarding} onClose={() => setOffboarding(null)}
        onStarted={(id) => { setOffboarding(null); setExpanded(id); refresh() }} />
    </div>
  )
}

function StartDialog({ car, onClose, onStarted }: { car: OffboardCar | null; onClose: () => void; onStarted: (id: string) => void }) {
  const letter = useGetOwnerOffboardingLetter<LetterPreview>()
  const start = useStartOwnerOffboarding<{ ok: boolean; id: string }>()
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const loadLetter = letter.trigger
  const vehicleId = car?.vehicle_id

  useEffect(() => {
    setNote(''); setError(null)
    if (vehicleId) void loadLetter({ vehicle_id: vehicleId }, { skipCache: true }).catch(() => undefined)
  }, [vehicleId, loadLetter])

  async function go() {
    if (!car) return
    setError(null)
    try {
      const r = await start.trigger({ vehicle_id: car.vehicle_id, note })
      if (r?.id) onStarted(r.id)
    } catch (e) { setError(message(e)) }
  }

  const preview = letter.data
  return (
    <Dialog open={car !== null} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Offboard {car?.plate}</DialogTitle>
          <DialogDescription>
            This starts the 30 days today and sends the owner the letter below straight away. The steps go on the task board, dated from today.
          </DialogDescription>
        </DialogHeader>
        {letter.loading && !preview ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Preparing the letter…</p>
        ) : letter.error ? (
          <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{letter.error}</p>
        ) : preview ? (
          <div className="space-y-2">
            <dl className="grid gap-1 text-xs">
              <div className="flex gap-2"><dt className="w-16 shrink-0 text-muted-foreground">To</dt><dd className="break-all font-medium text-foreground">{preview.to ?? 'No email address on file'}</dd></div>
              <div className="flex gap-2"><dt className="w-16 shrink-0 text-muted-foreground">Replies</dt><dd className="break-all text-foreground">{preview.reply_to ?? 'admin@fleetly.co.za'}</dd></div>
              <div className="flex gap-2"><dt className="w-16 shrink-0 text-muted-foreground">Subject</dt><dd className="text-foreground">{preview.subject}</dd></div>
            </dl>
            <pre className="max-h-80 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted/30 p-3 font-sans text-xs leading-relaxed text-foreground">{preview.body}</pre>
          </div>
        ) : null}
        <label className="block space-y-1 text-xs">
          <span className="text-muted-foreground">Note for the team (optional, not sent to the owner)</span>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="For example: owner asked to leave on the phone today" />
        </label>
        {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Back</Button>
          <Button variant="brand" disabled={start.loading || !preview?.to} onClick={() => void go()}>
            {start.loading ? <Spinner className="mr-1 h-4 w-4" /> : <Mail className="mr-1 h-4 w-4" />}
            Offboard and email the owner
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function CaseItem({ c, open, canAct, onToggle, onChanged }: {
  c: OffboardCase; open: boolean; canAct: boolean; onToggle: () => void; onChanged: () => void
}) {
  const overdue = c.steps.filter((s) => s.overdue).length
  return (
    <li>
      <button type="button" onClick={onToggle} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-accent/40">
        {open ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
        <span className="w-24 shrink-0 font-mono text-sm text-foreground">{c.plate}</span>
        <span className="min-w-[10rem] flex-1">
          <span className="block text-sm font-medium text-foreground">{c.owner_name ?? 'No owner on file'}</span>
          <span className="block text-xs text-muted-foreground">
            {c.closed_at
              ? `Closed ${formatDay(c.closed_at)}${c.exit_reason ? `, ${c.exit_reason}` : ''}`
              : `Day ${Math.min(c.day, 30)} of 30, ends ${formatDay(c.end_date)}`}
          </span>
        </span>
        <span className="flex flex-wrap items-center gap-1">
          {c.email_status && <Chip tone={c.email_status === 'sent' ? 'green' : c.email_status === 'queued' ? 'neutral' : 'red'}>{EMAIL_STATUS_LABEL[c.email_status]}</Chip>}
          {c.assessment && <Chip tone={c.assessment === 'major' ? 'amber' : 'green'}>{c.assessment === 'major' ? 'Heavy repairs, off the road' : 'Light repairs, operating'}</Chip>}
          {overdue > 0 && <Chip tone="red">{overdue} overdue</Chip>}
        </span>
      </button>
      {open && <CaseDetail c={c} canAct={canAct && !c.closed_at} onChanged={onChanged} />}
    </li>
  )
}

function CaseDetail({ c, canAct, onChanged }: { c: OffboardCase; canAct: boolean; onChanged: () => void }) {
  return (
    <div className="space-y-3 bg-muted/20 px-4 pb-4 text-sm sm:pl-11">
      <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
        <Row k="Car" v={[c.plate, c.vehicle_name].filter(Boolean).join(', ')} />
        <Row k="Manager" v={c.manager_name ?? 'None on file'} />
        <Row k="Offboarded" v={`${formatDay(c.notice_date)} by ${c.opened_by_name ?? '—'}`} />
        <Row k="Day 30" v={formatDay(c.end_date)} />
        <Row k="Owner letter" v={c.email_sent_at ? `${EMAIL_STATUS_LABEL[c.email_status ?? 'queued'] ?? 'Queued'} to ${c.owner_email}, ${formatWhen(c.email_sent_at)}${c.email_failure ? `: ${c.email_failure}` : ''}` : 'Not sent'} />
        <Row k="Insurance cover ends" v={c.insurance_cover_ends ? formatDay(c.insurance_cover_ends) : 'Cancellation not yet requested'} />
        {c.open_note && <Row k="Note" v={c.open_note} />}
        {c.parent_task_ref && <Row k="Task board" v={`#${c.parent_task_ref}`} />}
      </dl>

      {c.email_status === 'failed' && (
        <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">
          The letter to the owner did not go. Phone them today and check the address on the owner record.
        </p>
      )}

      <ol className="space-y-2">
        {c.steps.map((s) => <StepRow key={s.key} c={c} s={s} canAct={canAct} onChanged={onChanged} />)}
      </ol>

      {canAct && <CloseOrStop c={c} onChanged={onChanged} />}
      {c.closed_at && (
        <p className="text-xs text-muted-foreground">
          {c.closed_reason?.startsWith('stopped') ? 'Stopped' : 'Closed'} {formatWhen(c.closed_at)} by {c.closed_by_name ?? '—'}
          {c.exit_reason ? `. Left because: ${c.exit_reason}${c.exit_reason_note ? `, ${c.exit_reason_note}` : ''}` : ''}
          {c.closed_reason?.startsWith('stopped') ? `. ${c.closed_reason.slice(9)}` : ''}.
        </p>
      )}
    </div>
  )
}

function StepRow({ c, s, canAct, onChanged }: { c: OffboardCase; s: OffboardStep; canAct: boolean; onChanged: () => void }) {
  const done = s.done_at !== null || s.status === 'Done'
  const when = s.start === s.target ? formatDay(s.target) : `${formatDay(s.start)} to ${formatDay(s.target)}`
  return (
    <li className="rounded-md border border-border bg-background p-3">
      <div className="flex flex-wrap items-start gap-2">
        <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${done ? 'border-emerald-500 bg-emerald-500 text-white' : s.overdue ? 'border-rose-500' : 'border-border'}`}>
          {done && <Check className="h-3 w-3" />}
        </span>
        <span className="min-w-[12rem] flex-1">
          <span className="block text-sm font-medium text-foreground">{s.title}</span>
          <span className="block text-xs text-muted-foreground">
            {s.owner}, {when}
            {s.status === 'Cancelled' ? ', cancelled' : ''}
            {s.overdue ? ', overdue' : ''}
          </span>
          {done && s.done_at && s.key !== 'weekly' && (
            <span className="block text-xs text-foreground">Done {formatWhen(s.done_at)}{s.done_by_name ? ` by ${s.done_by_name}` : ''}{s.note && s.key !== 'assess' ? `: ${s.note}` : ''}</span>
          )}
        </span>
      </div>
      {s.key === 'assess' && c.assessment && (
        <dl className="mt-2 grid gap-1 pl-7 text-xs">
          <Row k="Where it is" v={c.assessment_location ?? '—'} />
          <Row k="What it needs" v={c.assessment_notes ?? '—'} />
        </dl>
      )}
      {s.key === 'weekly' && <WeeklyUpdates c={c} canAct={canAct && !done} onChanged={onChanged} />}
      {s.key === 'handback' && <OffboardingInspection c={c} canAct={canAct} onChanged={onChanged} />}
      {canAct && !done && s.key === 'assess' && <AssessForm c={c} onChanged={onChanged} />}
      {canAct && !done && (s.key === 'insurance' || s.key === 'repairs' || s.key === 'deinstall' || s.key === 'handback') && (
        <DateForm c={c} s={s} onChanged={onChanged} />
      )}
    </li>
  )
}

const DATE_PROMPT: Record<string, { label: string; button: string; placeholder: string }> = {
  insurance: { label: 'Asked the insurer on', button: 'Record the request', placeholder: 'Optional: who you asked, reference' },
  repairs: { label: 'Repairs finished on', button: 'Record repairs done', placeholder: 'Optional: what was done, where' },
  deinstall: { label: 'Unit removed on', button: 'Record the de-install', placeholder: 'Optional, or "no unit fitted"' },
  handback: { label: 'Handed back on', button: 'Record the hand back', placeholder: 'Optional: who collected it, condition' },
}

function DateForm({ c, s, onChanged }: { c: OffboardCase; s: OffboardStep; onChanged: () => void }) {
  const record = useRecordOwnerOffboardingStep()
  const [on, setOn] = useState(todaySast())
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const p = DATE_PROMPT[s.key]!
  async function go() {
    setError(null)
    try { await record.trigger({ id: c.id, step: s.key, on, note }); onChanged() } catch (e) { setError(message(e)) }
  }
  return (
    <div className="mt-2 space-y-1 pl-7">
      <div className="flex flex-wrap items-center gap-1">
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          {p.label}
          <Input type="date" className="h-8 w-36 text-xs" value={on} max={todaySast()} min={c.notice_date} onChange={(e) => setOn(e.target.value)} />
        </label>
        <Input className="h-8 min-w-[12rem] flex-1 text-xs" placeholder={p.placeholder} value={note} onChange={(e) => setNote(e.target.value)} />
        <Button size="sm" variant="outline" disabled={record.loading} onClick={() => void go()}>
          {record.loading && <Spinner className="mr-1 h-3.5 w-3.5" />}{p.button}
        </Button>
      </div>
      {s.key === 'insurance' && <p className="text-xs text-muted-foreground">The cover ends 30 days after the date you asked.</p>}
      {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}
    </div>
  )
}

function AssessForm({ c, onChanged }: { c: OffboardCase; onChanged: () => void }) {
  const assess = useAssessOwnerOffboarding()
  const [kind, setKind] = useState<'' | 'minor' | 'major'>('')
  const [where, setWhere] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  async function go() {
    setError(null)
    try { await assess.trigger({ id: c.id, assessment: kind, location: where, notes }); onChanged() } catch (e) { setError(message(e)) }
  }
  return (
    <div className="mt-2 space-y-2 pl-7 text-xs">
      <Input className="h-8 text-xs" placeholder="Where the car is now" value={where} onChange={(e) => setWhere(e.target.value)} />
      <Textarea className="min-h-[4rem] text-xs" placeholder="What needs fixing, mechanical and body" value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div className="flex flex-col gap-1 sm:flex-row sm:gap-4">
        <label className="flex items-start gap-2">
          <input type="radio" name={`kind-${c.id}`} checked={kind === 'minor'} onChange={() => setKind('minor')} className="mt-0.5" />
          <span><span className="font-medium text-foreground">Light repairs.</span> The car keeps operating while they are done.</span>
        </label>
        <label className="flex items-start gap-2">
          <input type="radio" name={`kind-${c.id}`} checked={kind === 'major'} onChange={() => setKind('major')} className="mt-0.5" />
          <span><span className="font-medium text-foreground">Heavy repairs</span>, such as panel damage. The car comes off the road now.</span>
        </label>
      </div>
      <Button size="sm" variant={kind === 'major' ? 'destructive' : 'outline'} disabled={assess.loading} onClick={() => void go()}>
        {assess.loading && <Spinner className="mr-1 h-3.5 w-3.5" />}
        {kind === 'major' ? 'Record and take it off the road' : 'Record the assessment'}
      </Button>
      {error && <p className="text-rose-700 dark:text-rose-300">{error}</p>}
    </div>
  )
}

function WeeklyUpdates({ c, canAct, onChanged }: { c: OffboardCase; canAct: boolean; onChanged: () => void }) {
  const record = useRecordOwnerOffboardingStep()
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  async function go() {
    setError(null)
    try { await record.trigger({ id: c.id, step: 'weekly', note: text }); setText(''); onChanged() } catch (e) { setError(message(e)) }
  }
  return (
    <div className="mt-2 space-y-1 pl-7 text-xs">
      {c.weekly_updates.length === 0 ? (
        <p className="text-muted-foreground">No update logged yet.</p>
      ) : (
        <ul className="space-y-1">
          {c.weekly_updates.map((u, i) => (
            <li key={i}><span className="text-muted-foreground">{formatWhen(u.at)}, {u.by ?? '—'}:</span> <span className="text-foreground">{u.body}</span></li>
          ))}
        </ul>
      )}
      {canAct && (
        <div className="flex flex-wrap items-center gap-1">
          <Input className="h-8 min-w-[12rem] flex-1 text-xs" placeholder="What the owner was told this week" value={text} onChange={(e) => setText(e.target.value)} />
          <Button size="sm" variant="outline" disabled={record.loading} onClick={() => void go()}>Log the update</Button>
        </div>
      )}
      {error && <p className="text-rose-700 dark:text-rose-300">{error}</p>}
    </div>
  )
}

function CloseOrStop({ c, onChanged }: { c: OffboardCase; onChanged: () => void }) {
  const close = useCloseOwnerOffboarding()
  const stop = useStopOwnerOffboarding()
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')
  const [stopping, setStopping] = useState(false)
  const [stopReason, setStopReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [stopped, setStopped] = useState(false)
  async function run(fn: () => Promise<unknown>) {
    setError(null)
    try { await fn(); onChanged() } catch (e) { setError(message(e)) }
  }
  return (
    <div className="space-y-2 rounded-md border border-border bg-background p-3 text-xs">
      {c.handed_back_on ? (
        <>
          <p className="text-sm font-medium text-foreground">Close the case</p>
          <p className="text-muted-foreground">The car was handed back on {formatDay(c.handed_back_on)}. Say why it left, and it is set to offboarded.</p>
          <div className="flex flex-wrap items-center gap-1">
            <NativeSelect className="h-8 w-44 text-xs" value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Why it left">
              <option value="">Why it left</option>
              {EXIT_REASONS.map((r) => <option key={r} value={r}>{r[0]!.toUpperCase() + r.slice(1)}</option>)}
            </NativeSelect>
            <Input className="h-8 min-w-[12rem] flex-1 text-xs" placeholder={reason === 'other' ? 'Say why, in a sentence' : 'Optional detail'} value={note} onChange={(e) => setNote(e.target.value)} />
            <Button size="sm" variant="brand" disabled={close.loading} onClick={() => void run(() => close.trigger({ id: c.id, reason, note }))}>Close the case</Button>
          </div>
        </>
      ) : (
        <p className="text-muted-foreground">The case closes once the car is handed back.</p>
      )}
      {!c.handed_back_on && (
        !stopping ? (
          <Button size="sm" variant="ghost" onClick={() => setStopping(true)}>The car is staying: stop this offboarding</Button>
        ) : (
          <div className="space-y-1">
            <p className="text-muted-foreground">
              The owner has already been emailed that the car is leaving. Stopping cancels the steps and puts the car back to active; write to the owner yourself
              {c.insurance_cancel_requested_on ? ', and withdraw the insurance cancellation, which was requested on ' + formatDay(c.insurance_cancel_requested_on) : ''}.
            </p>
            <div className="flex flex-wrap items-center gap-1">
              <Input className="h-8 min-w-[12rem] flex-1 text-xs" placeholder="Why the car is staying" value={stopReason} onChange={(e) => setStopReason(e.target.value)} />
              <Button size="sm" variant="destructive" disabled={stop.loading}
                onClick={() => void run(async () => { await stop.trigger({ id: c.id, reason: stopReason }); setStopped(true) })}>Stop it</Button>
              <Button size="sm" variant="ghost" onClick={() => setStopping(false)}>Back</Button>
            </div>
          </div>
        )
      )}
      {stopped && <p className="text-foreground">Stopped. Now write to the owner.</p>}
      {error && <p className="text-rose-700 dark:text-rose-300">{error}</p>}
    </div>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-32 shrink-0 text-muted-foreground">{k}</dt>
      <dd className="min-w-0 break-words text-foreground">{v}</dd>
    </div>
  )
}

